import { useEffect, useRef } from 'react';
import { STREAM_EVENT_TYPE } from './useKYCStream';
import type { StreamEvent } from './useKYCStream';
import { KYC_STATUS } from '../types/kyc';
import type { KYCStatus } from '../types/kyc';
import { SIMULATION_SCENARIO } from '../contexts/SimulationContext';
import type { SimulationScenario } from '../contexts/SimulationContext';

// ─────────────────────────────────────────────────────────────────────────────
// Delay en milisegundos para simular el tiempo de procesamiento del backend
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_PROCESSING_DELAY_MS = 1500;

interface UseSimulatedStreamOptions {
  status: KYCStatus;
  scenario: SimulationScenario;
  onEvent: (event: StreamEvent) => void;
}

// Construye el payload mock para la validación de ID según el escenario activo
function buildIdScorePayload(scenario: SimulationScenario): Record<string, unknown> {
  switch (scenario) {
    case SIMULATION_SCENARIO.ID_REQUIRES_POA:
    case SIMULATION_SCENARIO.POA_SUCCESS:
    case SIMULATION_SCENARIO.POA_CONDITIONAL:
    case SIMULATION_SCENARIO.POA_FAILURE:
      // Estos escenarios pasan la ID con éxito y requieren comprobante de domicilio
      return {
        score: 85,
        passed: true,
        requiresPOA: true,
        details: { mock: true, check: 'id_requires_poa' },
      };

    case SIMULATION_SCENARIO.ID_FAILURE:
      return {
        score: 42,
        passed: false,
        requiresPOA: false,
        rejectionReason: 'doc_low_score',
        details: {},
      };

    case SIMULATION_SCENARIO.ID_EXPIRED:
      return {
        score: 90,
        passed: false,
        requiresPOA: false,
        rejectionReason: 'doc_expired',
        details: {},
      };

    case SIMULATION_SCENARIO.ID_SUCCESS:
    default:
      return {
        score: 88,
        passed: true,
        requiresPOA: false,
        details: { mock: true, check: 'id_success' },
      };
  }
}

// Construye el payload mock para la validación del comprobante de domicilio
function buildPoaScorePayload(scenario: SimulationScenario): Record<string, unknown> {
  switch (scenario) {
    case SIMULATION_SCENARIO.POA_CONDITIONAL:
      return {
        score: 52,
        passed: false,
        requiresPOA: false,
        rejectionReason: 'doc_low_score',
        details: { mock: true, check: 'poa_conditional' },
      };

    case SIMULATION_SCENARIO.POA_FAILURE:
      return {
        score: 28,
        passed: false,
        requiresPOA: false,
        rejectionReason: 'doc_low_score',
        details: { mock: true, check: 'poa_failure' },
      };

    case SIMULATION_SCENARIO.ID_REQUIRES_POA:
    case SIMULATION_SCENARIO.POA_SUCCESS:
    default:
      return {
        score: 75,
        passed: true,
        requiresPOA: false,
        details: { mock: true, check: 'poa_success' },
      };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook principal — reemplaza useKYCStream en modo simulación
// ─────────────────────────────────────────────────────────────────────────────
//
// Observa el status del reducer y dispara eventos mock con delay artificial
// para simular el comportamiento asíncrono del backend. Cuando onEvent es
// un no-op (simulación desactivada), el hook no hace nada.
//
export function useSimulatedStream({
  status,
  scenario,
  onEvent,
}: UseSimulatedStreamOptions): void {
  // Patrón ref para evitar stale closures — mismo enfoque que useKYCStream
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  });

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | null = null;

    if (status === KYC_STATUS.VALIDATING_ID) {
      // Simular procesamiento de documento de identidad
      timerId = setTimeout(() => {
        const event: StreamEvent = {
          type: STREAM_EVENT_TYPE.SCORE_COMPLETE,
          sessionId: '__simulation__',
          payload: buildIdScorePayload(scenario),
        };
        onEventRef.current(event);
      }, MOCK_PROCESSING_DELAY_MS);
    } else if (status === KYC_STATUS.AWAITING_EXTRACTION_SCORE) {
      // D-7: simular el pipeline backend (extraction → validation) como gate de score.
      // El escenario DATA_MISMATCH emite extraction_rejected con reason: data_mismatch.
      // El resto de escenarios emiten validation_complete (aprobado).
      timerId = setTimeout(() => {
        let event: StreamEvent;
        if (scenario === SIMULATION_SCENARIO.DATA_MISMATCH) {
          event = {
            type: STREAM_EVENT_TYPE.EXTRACTION_REJECTED,
            sessionId: '__simulation__',
            payload: { score: 88, passed: false, rejectionReason: 'data_mismatch' },
          };
        } else {
          event = {
            type: STREAM_EVENT_TYPE.VALIDATION_COMPLETE,
            sessionId: '__simulation__',
            payload: { status: 'approved', score: 88, details: { mock: true } },
          };
        }
        onEventRef.current(event);
      }, MOCK_PROCESSING_DELAY_MS);
    } else if (status === KYC_STATUS.VALIDATING_POA) {
      // Simular procesamiento de comprobante de domicilio
      timerId = setTimeout(() => {
        const event: StreamEvent = {
          type: STREAM_EVENT_TYPE.SCORE_COMPLETE,
          sessionId: '__simulation__',
          payload: buildPoaScorePayload(scenario),
        };
        onEventRef.current(event);
      }, MOCK_PROCESSING_DELAY_MS);
    }

    // Cancelar el timer pendiente al desmontar o cuando cambie el status/scenario
    return () => {
      if (timerId !== null) {
        clearTimeout(timerId);
      }
    };
  }, [status, scenario]);
}
